import { DateTime, Interval } from 'luxon';


export enum CustomFormat {
    YYYYMMDD = 'YYYYMMDD',
    DDMMYYY = 'DDMMYYY'
}

// Settings.defaultLocale = "en-AU";


export class DateUtility {

    public static readonly minDateTime = DateTime.fromISO('1900-01-01T00:00:00.000Z');
    public static readonly maxDateTime = DateTime.fromISO('2100-12-31T23:59:59.999Z');

    public static getStartOfDay(value: DateTime | Date | string, timezone = 'utc'): DateTime {
        return this.getDateTime(value, timezone).startOf('day');
    }

    public static getEndOfDay(value: DateTime | Date | string, timezone = 'utc'): DateTime {
        return this.getDateTime(value, timezone).endOf('day');
    }

    public static getDateTime(value: DateTime | Date | string | number, timezone = 'utc', keepLocalTime = false): DateTime {

        let date: DateTime = null;
        if (value instanceof Date) {
            date = DateTime.fromJSDate(value);
        } else if (typeof value === 'number') {
            date = DateTime.fromMillis(value);
        } else if (typeof (value) === 'string' || value instanceof String) {
            date = DateTime.fromISO(value.toString());
        } else if (value instanceof DateTime) {
            date = value;
        }

        if (date) {
            timezone = timezone ?? 'utc';
            date = date.setZone(timezone, { keepLocalTime: keepLocalTime });
        }

        return date;
    }

    public static getDateTimeFromISO(value: string, timezone = 'utc'): DateTime {
        return DateTime.fromISO(value, { zone: timezone });
    }



    /**
     * Given a datetime and a timezone.
     * Convert it to the appropriate time.
     * Then, set the timezone to UTC and keep the local time.
     */
    public static getTimezoneIndependentDateTime(value: DateTime | Date | string, timezone: string): DateTime {

        //This date has been correctly converted to the appropriate timezone
        const date = this.getDateTime(value, timezone);
        //Now, we want to construct this time as a UTC time, with everything the same
        const tzIndependent = date.setZone('utc', { keepLocalTime: true });
        return tzIndependent;

    }

    public static fromFormat(value: string, format: string, timezone = 'utc', keepLocalTime = false, formatLocale: string = ''): DateTime {

        if (!value || value === '')
            return null;

        let date: DateTime = null;
        if (formatLocale)
            date = DateTime.fromFormat(value, format, { locale: formatLocale });
        else
            date = DateTime.fromFormat(value, format);

        if (date) {
            date = date.setZone(timezone, { keepLocalTime: keepLocalTime });
        }

        return date;
    }

    /**
     * Only works client side, on server, it will be the server local timezone
     */
    public static getLocalToday(): DateTime {
        return DateTime.local().setZone(this.getLocalTimeZone());
    }

    /**
     * Method to ensure a day for a specific month makes sense
     * This method is not useful for when you want to ADD days, its only for setting days
     */
    public static getValidDayForMonthAndYear(day: number, month: number, year: number): number {
        let saneDay = day;
        if (month == 2 && day > 28) {
            saneDay = this.isLeapYear(year) ? 29 : 28;
        }
        else if ((month == 4 || month == 6 || month == 9 || month == 11) && day > 30)
            saneDay = 30;
        else if (day > 31)
            saneDay = 31;
        return saneDay;
    }

    public static isLeapYear(year: number): boolean {
        let leapYear = false;
        if ((year % 4) == 0) {
            if ((year % 100) == 0) {
                if ((year % 400) == 0) {
                    leapYear = true;
                }
            }
            else {
                leapYear = true;
            }
        }
        else {
            leapYear = false;
        }
        return leapYear;
    }

    public static getDate(value: DateTime | Date | string): Date {

        let date: Date = null;

        if (value instanceof Date) {
            date = value;
        } else if (typeof (value) === 'string' || value instanceof String) {
            date = new Date(value as string);
        } else if (value instanceof DateTime) {
            date = new Date(value.toJSDate());
        }

        return date;
    }

    public static getDateByAddMoreHours(value: DateTime | Date | string, hours: number): Date {

        let date: Date = null;

        if (value instanceof Date) {
            date = value;
        } else if (typeof (value) === 'string' || value instanceof String) {
            date = new Date(value as string);
        } else if (value instanceof DateTime) {
            date = new Date(value.toJSDate());
        }



        return new Date(date.getTime() + hours * 60 * 60 * 1000);
    }

    public static formatDateTime(value: DateTime | Date | string, timezone: string, dateFormat: string): string {
        const dt = this.getDateTime(value, timezone);

        return dt
            ? dt.toFormat(dateFormat)
            : null;
    }

    public static formatShortDate(value: DateTime | Date | string, timezone: string): string {
        const dt = this.getDateTime(value, timezone);

        return dt
            ? dt.toLocaleString(DateTime.DATE_SHORT)
            : null;
    }

    public static formatSimpleTime(value: DateTime | Date | string, timezone: string): string {

        const dt = this.getDateTime(value, timezone);

        return dt
            ? dt.toLocaleString(DateTime.TIME_SIMPLE)
            : null;
    }

    public static formatSimpleTime24(value: DateTime | Date | string, timezone: string): string {

        const dt = this.getDateTime(value, timezone);

        return dt
            ? dt.toLocaleString(DateTime.TIME_24_SIMPLE)
            : null;
    }

    public static customFormat(value: DateTime | Date | string, timezone: string, format: CustomFormat): string {

        const dt = this.getDateTime(value, timezone);

        const day = dt.get('day') < 10
            ? `0${dt.get('day')}`
            : `${dt.get('day')}`;

        const month = dt.get('month') < 10
            ? `0${dt.get('month')}`
            : `${dt.get('month')}`;

        const year = dt.get('year');

        switch (format) {
            case CustomFormat.YYYYMMDD: {
                return `${year}${month}${day}`;
            }
            case CustomFormat.DDMMYYY: {
                return `${day}${month}${year}`;
            }
        }


    }

    public static getNowAsUtcWithCurrentTime(): DateTime {
        const now = new Date().toISOString();
        const nowAsUtc = DateTime
            .fromISO(now)
            .setZone(DateUtility.getLocalTimeZone())
            .setZone('utc', { keepLocalTime: true });
        return nowAsUtc;
    }

    public static getTodayAsUtcWithCurrentTime(): DateTime {
        const now = new Date().toISOString();
        const nowAsUtc = DateTime
            .fromISO(now)
            .setZone(DateUtility.getLocalTimeZone())
            .set({ hour: 0, minute: 0, second: 0 })
            .setZone('utc', { keepLocalTime: true });
        return nowAsUtc;
    }

    public static getLocalTimeZone(): string {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return timezone;
    }

    public static getDateTimeAsUtc(value: DateTime): DateTime {

        const asUtc = DateTime
            .fromISO(value.toString())
            .setZone(DateUtility.getLocalTimeZone())
            .setZone('utc', { keepLocalTime: true });

        return asUtc;
    }

    public static getSimpleTime(value: DateTime | Date | string, timezone = 'utc'): string {

        let date: DateTime = null;

        if (value instanceof Date) {
            date = DateTime.fromJSDate(value);
        } else if (typeof (value) === 'string' || value instanceof String) {
            date = DateTime.fromISO(value.toString());
        } else if (value instanceof DateTime) {
            date = value;
        }

        if (date) {
            date = date.setZone(timezone);
        }

        return date.toLocaleString(DateTime.TIME_SIMPLE);
    }

    public static getDateMed(value: DateTime | Date | string, timezone = 'utc'): string {

        let date: DateTime = null;

        if (value instanceof Date) {
            date = DateTime.fromJSDate(value);
        } else if (typeof (value) === 'string' || value instanceof String) {
            date = DateTime.fromISO(value.toString());
        } else if (value instanceof DateTime) {
            date = value;
        }

        if (date) {
            date = date.setZone(timezone);
        }

        return date.toLocaleString(DateTime.DATE_MED);
    }

    private static convertToDateTime(value: DateTime | Date | string, timezone = 'utc') {
        let date: DateTime = null;

        if (value instanceof Date) {
            date = DateTime.fromJSDate(value);
        } else if (typeof (value) === 'string' || value instanceof String) {
            date = DateTime.fromISO(value.toString());
        } else if (value instanceof DateTime) {
            date = value;
        }

        if (date) {
            date = date.setZone(timezone);
        }
        return date;
    }

    /**
     * Given two date objects, take the date from one and the time from the other
     * and create a new date
     * The time parameter is ALWAYS assumed to be UTC as we do not want to do any conversion here
     */
    public static setDateAndTimeFromValues(value: DateTime | Date | string, timeValue: DateTime | Date | string, timezone = 'utc'): DateTime {
        let date: DateTime = null;

        date = this.convertToDateTime(value, timezone);

        let time: DateTime = null;
        time = this.convertToDateTime(timeValue, 'utc');

        date = date.set({
            hour: time.hour,
            minute: time.minute
        });
        return date;

    }

    public static calcStartOfDayToEndTime(date: DateTime): number {
        return Interval.fromDateTimes(date.startOf('day'), date).length('second');
    }

    public static calcStartTimeToEndOfDay(date: DateTime): number {
        return Interval.fromDateTimes(date, date.endOf('day')).length('second');
    }

}

